import React, { useState, useRef } from 'react';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { useLocation } from 'wouter';
import { 
  ChevronLeft, 
  ChevronRight, 
  Maximize, 
  Minimize, 
  X, 
  Play, 
  CheckCircle2, 
  FileText, 
  ExternalLink 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Card } from '@/components/ui/card';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

// Custom components for the lecture
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-6 rounded-r">
      <div className="flex items-start">
        <CheckCircle2 className="h-6 w-6 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-blue-900">{children}</div>
      </div>
    </div>
  );
};

const ScientificDiagram = ({ 
  title, 
  caption, 
  children 
}: { 
  title: string; 
  caption: string; 
  children: React.ReactNode;
}) => {
  return (
    <figure className="my-8 bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
      <div className="p-4">
        <figcaption className="text-sm text-gray-600 mb-2 italic">Figure {title}</figcaption>
        {children}
        <div className="mt-4 text-sm text-gray-600">{caption}</div>
      </div>
    </figure>
  );
};

interface Slide {
  id: number;
  title: string;
  content: React.ReactNode;
  image?: string;
}

const Slideshow = ({ slides }: { slides: Slide[] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const slideshowRef = useRef<HTMLDivElement>(null);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      slideshowRef.current?.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  // Listen for fullscreen changes
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  return (
    <div className="mb-8">
      <div 
        ref={slideshowRef}
        className={`relative bg-white ${isFullscreen ? 'fixed inset-0 z-50 flex flex-col justify-center overflow-auto p-4' : 'border rounded-lg'}`}
      >
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-medium">Lecture Slideshow</h3>
          <div className="flex space-x-2">
            <Button 
              variant="outline" 
              size="icon" 
              onClick={toggleFullscreen}
            >
              {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
            </Button>
            {isFullscreen && (
              <Button 
                variant="outline" 
                size="icon" 
                onClick={() => document.exitFullscreen()}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-6">{slides[currentSlide].title}</h2>
          <div className="prose max-w-none">
            {slides[currentSlide].content}
          </div>
        </div>
        
        <div className="flex justify-between items-center p-4 border-t mt-auto">
          <Button 
            variant="outline" 
            onClick={prevSlide}
            className="flex items-center"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>
          <div className="text-sm text-gray-500">
            Slide {currentSlide + 1} of {slides.length}
          </div>
          <Button 
            variant="outline" 
            onClick={nextSlide}
            className="flex items-center"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};



function PeroxisomesHealthDisease() {
  const [location] = useLocation();



  const lectureSlides: Slide[] = [
    {
      id: 1,
      title: "Introduction to Peroxisomes in Health and Disease",
      content: (
        <>
          <p>Peroxisomes are small, membrane-bound organelles found in virtually all eukaryotic cells. They play critical roles in multiple metabolic pathways, particularly in lipid metabolism and reactive oxygen species (ROS) detoxification. Despite their relatively small size compared to mitochondria, peroxisomes are essential for cellular homeostasis and human health.</p>
          
          <p>In this lecture, we will explore the fundamental biology of peroxisomes, their core metabolic functions, their interactions with other cellular organelles, and the clinical implications of peroxisomal dysfunction. We will also examine current diagnostic approaches, therapeutic strategies, and cutting-edge research in peroxisomal medicine.</p>
          
          <KeyTakeaway>
            Few organelles in the cell embody such a critical balancing act between survival and pathology as peroxisomes. Their involvement in fatty acid metabolism, ROS detoxification, plasmalogen synthesis, and bile acid metabolism positions them as key players in metabolic health and disease.
          </KeyTakeaway>
          
          <p>By understanding peroxisomal function at the molecular, cellular, and systems levels, clinicians and researchers can develop more targeted interventions for conditions ranging from rare genetic disorders like X-linked adrenoleukodystrophy (X-ALD) to more common conditions like non-alcoholic fatty liver disease (NAFLD) that may involve peroxisomal dysfunction.</p>
        </>
      )
    },
    {
      id: 2,
      title: "Historical Context and Structural Overview",
      content: (
        <>
          <p>The Belgian cytologist Christian de Duve coined the term "peroxisome" in 1954 after discovering these organelles during his groundbreaking work on cellular fractions. He noted that they were sites where hydrogen peroxide (H₂O₂) was both generated and degraded, giving rise to the name "peroxisome."</p>
          
          <ScientificDiagram 
            title="1. Peroxisome Structure and Biogenesis" 
            caption="Basic structure of a peroxisome showing key components and biogenesis pathways. A) Single membrane enclosing a granular matrix. B) Import machinery composed of PEX proteins. C) Peroxisome biogenesis pathways, including de novo formation from ER and growth/division of existing peroxisomes."
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 550" className="w-full" style={{ maxHeight: '450px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="550" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Peroxisome Structure and Biogenesis</text>
                
                {/* A. Peroxisome Structure */}
                <g transform="translate(200, 160)">
                  {/* Background */}
                  <rect x="-120" y="-70" width="240" height="190" rx="10" fill="#F5EEF8" stroke="#8E44AD" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-50" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#8E44AD">A. Peroxisome Structure</text>
                  
                  {/* Peroxisome */}
                  <circle cx="0" cy="20" r="60" fill="#F8F9F9" stroke="#8E44AD" strokeWidth="2" />
                  
                  {/* Matrix */}
                  <g>
                    <circle cx="-20" cy="10" r="5" fill="#D2B4DE" />
                    <circle cx="10" cy="30" r="5" fill="#D2B4DE" />
                    <circle cx="-15" cy="40" r="5" fill="#D2B4DE" />
                    <circle cx="20" cy="-5" r="5" fill="#D2B4DE" />
                    <circle cx="-35" cy="-15" r="5" fill="#D2B4DE" />
                    <circle cx="30" cy="15" r="5" fill="#D2B4DE" />
                  </g>
                  
                  {/* Membrane proteins */}
                  <circle cx="-40" cy="46" r="10" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="-40" y="49" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#8E44AD">PMP</text>
                  
                  <circle cx="40" cy="46" r="10" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="40" y="49" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#8E44AD">ABC</text>
                  
                  {/* Matrix Enzyme labels */}
                  <text x="0" y="95" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#333">Key Enzymes:</text>
                  <text x="0" y="110" textAnchor="middle" fontSize="12" fill="#333">Catalase, ACOX, DHAPAT</text>
                </g>
                
                {/* B. Import Machinery */}
                <g transform="translate(600, 160)">
                  {/* Background */}
                  <rect x="-120" y="-70" width="240" height="190" rx="10" fill="#EBF5FB" stroke="#3498DB" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-50" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#3498DB">B. Import Machinery</text>
                  
                  {/* Peroxisome */}
                  <circle cx="0" cy="20" r="60" fill="#F8F9F9" stroke="#3498DB" strokeWidth="2" />
                  
                  {/* Docking complex */}
                  <rect x="-20" y="-55" width="40" height="15" rx="5" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="0" y="-45" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">PEX14</text>
                  
                  {/* PTS1 Receptor */}
                  <g transform="translate(-40, -20)">
                    <circle cx="0" cy="0" r="12" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                    <text x="0" y="3" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#3498DB">PEX5</text>
                    
                    <circle cx="-20" cy="0" r="8" fill="#F9E79F" stroke="#F1C40F" strokeWidth="1.5" />
                    <text x="-20" y="3" textAnchor="middle" fontSize="7" fontWeight="bold" fill="#F1C40F">PTS1</text>
                  </g>
                  
                  {/* PTS2 Receptor */}
                  <g transform="translate(40, -20)">
                    <circle cx="0" cy="0" r="12" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                    <text x="0" y="3" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#3498DB">PEX7</text>
                    
                    <circle cx="20" cy="0" r="8" fill="#F9E79F" stroke="#F1C40F" strokeWidth="1.5" />
                    <text x="20" y="3" textAnchor="middle" fontSize="7" fontWeight="bold" fill="#F1C40F">PTS2</text>
                  </g>
                  
                  {/* Translocation channel */}
                  <rect x="-15" y="45" width="30" height="15" rx="7" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="0" y="55" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#3498DB">RING</text>
                  
                  {/* Labels */}
                  <text x="0" y="95" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#333">Import Pathways:</text>
                  <text x="0" y="110" textAnchor="middle" fontSize="12" fill="#333">PTS1/PEX5 and PTS2/PEX7</text>
                </g>
                
                {/* C. Biogenesis */}
                <g transform="translate(400, 360)">
                  {/* Background */}
                  <rect x="-320" y="-50" width="640" height="120" rx="10" fill="#f9f9f9" stroke="#333" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-25" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#111">C. Peroxisome Biogenesis Pathways</text>
                  
                  {/* De Novo from ER */}
                  <g transform="translate(-200, 20)">
                    <rect x="-70" y="-30" width="140" height="60" rx="5" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                    
                    {/* ER */}
                    <rect x="-50" y="-20" width="100" height="10" rx="5" fill="#F5B7B1" stroke="#E74C3C" strokeWidth="1" />
                    <text x="0" y="-12" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#922B21">ER</text>
                    
                    {/* Budding vesicle */}
                    <circle cx="-30" cy="0" r="8" fill="#F5B7B1" stroke="#E74C3C" strokeWidth="1" />
                    <text x="-30" y="3" textAnchor="middle" fontSize="7" fontWeight="bold" fill="#922B21">PPV</text>
                    
                    {/* Pre-peroxisome */}
                    <circle cx="0" cy="0" r="10" fill="#F5B7B1" stroke="#E74C3C" strokeWidth="1" />
                    <text x="0" y="3" textAnchor="middle" fontSize="7" fontWeight="bold" fill="#922B21">Pre</text>
                    
                    {/* Mature peroxisome */}
                    <circle cx="30" cy="0" r="15" fill="#F8F9F9" stroke="#E74C3C" strokeWidth="1.5" />
                    <text x="30" y="0" textAnchor="middle" fontSize="7" fontWeight="bold" fill="#922B21">Mature</text>
                    
                    <text x="0" y="25" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#922B21">De Novo Formation</text>
                  </g>
                  
                  {/* Growth and Division */}
                  <g transform="translate(200, 20)">
                    <rect x="-70" y="-30" width="140" height="60" rx="5" fill="#D4EFDF" stroke="#27AE60" strokeWidth="1.5" />
                    
                    {/* Mature peroxisome */}
                    <circle cx="-40" cy="0" r="15" fill="#F8F9F9" stroke="#27AE60" strokeWidth="1.5" />
                    <text x="-40" y="0" textAnchor="middle" fontSize="7" fontWeight="bold" fill="#196F3D">Mature</text>
                    
                    {/* Growth */}
                    <circle cx="0" cy="0" r="20" fill="#F8F9F9" stroke="#27AE60" strokeWidth="1.5" />
                    <text x="0" y="0" textAnchor="middle" fontSize="7" fontWeight="bold" fill="#196F3D">Growth</text>
                    <path d="M-20,0 L-15,0" stroke="#196F3D" strokeWidth="1" markerEnd="url(#greenArrow)" />
                    
                    {/* Division */}
                    <circle cx="35" cy="-10" r="10" fill="#F8F9F9" stroke="#27AE60" strokeWidth="1.5" />
                    <circle cx="35" cy="10" r="10" fill="#F8F9F9" stroke="#27AE60" strokeWidth="1.5" />
                    <path d="M20,0 L25,0" stroke="#196F3D" strokeWidth="1" markerEnd="url(#greenArrow)" />
                    
                    <text x="0" y="25" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#196F3D">Growth and Division</text>
                  </g>
                </g>
                
                {/* Connecting arrows */}
                <path d="M200,210 L300,310" stroke="#8E44AD" strokeWidth="2" markerEnd="url(#purpleArrow)" />
                <path d="M600,210 L500,310" stroke="#3498DB" strokeWidth="2" markerEnd="url(#blueArrow)" />
                
                {/* Arrow definitions */}
                <defs>
                  <marker id="greenArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#196F3D" />
                  </marker>
                  <marker id="purpleArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#8E44AD" />
                  </marker>
                  <marker id="blueArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#3498DB" />
                  </marker>
                </defs>
              </svg>
            </div>
          </ScientificDiagram>
          
          <p>Peroxisomes are small (0.1-1.0 µm in diameter), spherical or ovoid organelles with a single phospholipid bilayer membrane. They contain a granular matrix of enzymes involved in various metabolic processes. Their size and number vary considerably depending on tissue type and metabolic demands, with hepatocytes (liver cells) containing particularly abundant peroxisomes due to their central role in lipid metabolism and detoxification.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">Membrane Composition</h3>
              <p>The peroxisomal membrane contains various integral proteins, including ATP-binding cassette (ABC) transporters that facilitate the movement of metabolites into the organelle. Peroxisomal membrane proteins (PMPs) also anchor docking and receptor complexes required for protein import.</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="font-medium text-indigo-900 mb-2">Biogenesis Pathways</h3>
              <p>Peroxisomes can form through two main pathways: de novo formation from the endoplasmic reticulum (ER) or growth and division of pre-existing peroxisomes. Both pathways involve peroxin (PEX) proteins, which are essential for peroxisome assembly and maintenance.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Peroxisomal proteins are synthesized on free ribosomes in the cytosol and then imported into the organelle post-translationally. This import process is orchestrated by peroxins (PEX proteins) and involves recognizing specific targeting signals (PTS1 or PTS2) on peroxisomal matrix proteins. Mutations in PEX genes lead to peroxisomal biogenesis disorders, such as the Zellweger spectrum disorders.
          </KeyTakeaway>
          
          <p>Peroxisome turnover involves a selective form of autophagy called pexophagy, which removes damaged or superfluous peroxisomes. This quality control process is crucial for preventing the accumulation of dysfunctional peroxisomes that could generate unregulated oxidative stress.</p>
        </>
      )
    },
    {
      id: 3,
      title: "Core Metabolic Roles",
      content: (
        <>
          <p>Peroxisomes play crucial roles in multiple metabolic pathways, particularly in lipid metabolism and reactive oxygen species (ROS) detoxification. Understanding these core functions is essential for appreciating the clinical manifestations of peroxisomal disorders.</p>
          
          <ScientificDiagram 
            title="2. Peroxisomal Metabolic Pathways" 
            caption="Major metabolic pathways in peroxisomes. A) β-oxidation of very-long-chain fatty acids (VLCFAs) and branched-chain fatty acids. B) Reactive oxygen species (ROS) metabolism, highlighting the generation and detoxification of hydrogen peroxide. C) Plasmalogen synthesis pathway, showing the initial steps that occur in peroxisomes. D) Bile acid synthesis pathway, showing the involvement of peroxisomal β-oxidation in side-chain shortening."
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 550" className="w-full" style={{ maxHeight: '450px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="550" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Peroxisomal Metabolic Pathways</text>
                
                {/* A. β-Oxidation */}
                <g transform="translate(200, 140)">
                  {/* Background */}
                  <rect x="-120" y="-70" width="240" height="160" rx="10" fill="#F5EEF8" stroke="#8E44AD" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-50" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#8E44AD">A. β-Oxidation</text>
                  
                  {/* VLCFA */}
                  <rect x="-100" y="-20" width="50" height="15" rx="3" fill="#D2B4DE" />
                  <text x="-75" y="-10" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#8E44AD">VLCFA</text>
                  
                  {/* ACOX (Rate-limiting enzyme) */}
                  <rect x="-15" y="-20" width="30" height="30" rx="15" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="0" y="-8" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#8E44AD">ACOX</text>
                  
                  {/* H2O2 Production */}
                  <rect x="50" y="-25" width="40" height="15" rx="3" fill="#F5B7B1" />
                  <text x="70" y="-15" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#E74C3C">H₂O₂</text>
                  
                  {/* Additional β-Oxidation steps */}
                  <rect x="-15" y="20" width="30" height="30" rx="15" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="0" y="32" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#8E44AD">DBP</text>
                  
                  <rect x="-15" y="60" width="30" height="30" rx="15" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="0" y="72" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#8E44AD">THIO</text>
                  
                  {/* Medium-chain fatty acid product */}
                  <rect x="50" y="65" width="50" height="15" rx="3" fill="#D2B4DE" />
                  <text x="75" y="75" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#8E44AD">MCFA</text>
                  
                  {/* Connection arrows */}
                  <path d="M-50,-12 L-20,-5" stroke="#8E44AD" strokeWidth="1.5" markerEnd="url(#purpleArrow)" />
                  <path d="M15,-5 L50,-17" stroke="#E74C3C" strokeWidth="1.5" markerEnd="url(#redArrow)" />
                  <path d="M0,10 L0,20" stroke="#8E44AD" strokeWidth="1.5" markerEnd="url(#purpleArrow)" />
                  <path d="M0,50 L0,60" stroke="#8E44AD" strokeWidth="1.5" markerEnd="url(#purpleArrow)" />
                  <path d="M15,75 L50,72" stroke="#8E44AD" strokeWidth="1.5" markerEnd="url(#purpleArrow)" />
                  
                  {/* To mitochondria */}
                  <path d="M75,65 L90,40" stroke="#27AE60" strokeWidth="1.5" strokeDasharray="4,2" />
                  <text x="100" y="35" textAnchor="middle" fontSize="10" fill="#27AE60">To Mitochondria</text>
                </g>
                
                {/* B. ROS Metabolism */}
                <g transform="translate(600, 140)">
                  {/* Background */}
                  <rect x="-120" y="-70" width="240" height="160" rx="10" fill="#EBF5FB" stroke="#3498DB" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-50" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#3498DB">B. ROS Metabolism</text>
                  
                  {/* H2O2 from β-oxidation */}
                  <rect x="-100" y="-20" width="40" height="15" rx="3" fill="#F5B7B1" />
                  <text x="-80" y="-10" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#E74C3C">H₂O₂</text>
                  
                  {/* Catalase */}
                  <rect x="-15" y="-20" width="30" height="30" rx="15" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                  <text x="0" y="-8" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#3498DB">CAT</text>
                  
                  {/* Water and Oxygen */}
                  <rect x="50" y="-25" width="30" height="15" rx="3" fill="#AED6F1" />
                  <text x="65" y="-15" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">H₂O</text>
                  
                  <rect x="50" y="-5" width="30" height="15" rx="3" fill="#AED6F1" />
                  <text x="65" y="5" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">O₂</text>
                  
                  {/* Other Antioxidant Enzymes */}
                  <rect x="-80" y="40" width="40" height="15" rx="3" fill="#D6EAF8" />
                  <text x="-60" y="50" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#3498DB">SOD</text>
                  
                  <rect x="-20" y="40" width="40" height="15" rx="3" fill="#D6EAF8" />
                  <text x="0" y="50" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#3498DB">GPX</text>
                  
                  <rect x="40" y="40" width="40" height="15" rx="3" fill="#D6EAF8" />
                  <text x="60" y="50" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#3498DB">PRDX</text>
                  
                  {/* Additional Info */}
                  <text x="0" y="80" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#3498DB">Antioxidant Defense</text>
                  
                  {/* Connection arrows */}
                  <path d="M-60,-12 L-20,-5" stroke="#3498DB" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
                  <path d="M15,-5 L50,-17" stroke="#3498DB" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
                  <path d="M15,0 L50,3" stroke="#3498DB" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
                </g>
                
                {/* C. Plasmalogen Synthesis */}
                <g transform="translate(200, 350)">
                  {/* Background */}
                  <rect x="-120" y="-50" width="240" height="120" rx="10" fill="#f9f9f9" stroke="#F1C40F" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-30" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#F1C40F">C. Plasmalogen Synthesis</text>
                  
                  {/* Starting material */}
                  <rect x="-90" y="0" width="40" height="15" rx="3" fill="#FCF3CF" />
                  <text x="-70" y="10" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#F1C40F">DHAP</text>
                  
                  {/* DHAPAT */}
                  <circle cx="-20" cy="0" r="15" fill="#FCF3CF" stroke="#F1C40F" strokeWidth="1.5" />
                  <text x="-20" y="3" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#F1C40F">DHAPAT</text>
                  
                  {/* Alkyl-DHAP */}
                  <rect x="30" y="0" width="60" height="15" rx="3" fill="#FCF3CF" />
                  <text x="60" y="10" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#F1C40F">Alkyl-DHAP</text>
                  
                  {/* To ER */}
                  <path d="M90,0 L110,0" stroke="#F1C40F" strokeWidth="1.5" strokeDasharray="4,2" />
                  <text x="90" y="20" textAnchor="middle" fontSize="10" fill="#F1C40F">To ER</text>
                  
                  {/* Connection arrows */}
                  <path d="M-50,8 L-35,3" stroke="#F1C40F" strokeWidth="1.5" markerEnd="url(#yellowArrow)" />
                  <path d="M-5,0 L30,7" stroke="#F1C40F" strokeWidth="1.5" markerEnd="url(#yellowArrow)" />
                  
                  {/* Additional Info */}
                  <text x="0" y="40" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#F1C40F">Initial Steps in Peroxisomes</text>
                </g>
                
                {/* D. Bile Acid Synthesis */}
                <g transform="translate(600, 350)">
                  {/* Background */}
                  <rect x="-120" y="-50" width="240" height="120" rx="10" fill="#f9f9f9" stroke="#27AE60" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-30" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#27AE60">D. Bile Acid Synthesis</text>
                  
                  {/* Bile Acid Intermediates */}
                  <rect x="-90" y="0" width="60" height="15" rx="3" fill="#D4EFDF" />
                  <text x="-60" y="10" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#27AE60">C27-BA</text>
                  
                  {/* Peroxisomal β-oxidation */}
                  <circle cx="0" cy="0" r="15" fill="#D4EFDF" stroke="#27AE60" strokeWidth="1.5" />
                  <text x="0" y="3" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#27AE60">β-Oxid</text>
                  
                  {/* Mature Bile Acids */}
                  <rect x="40" y="0" width="60" height="15" rx="3" fill="#D4EFDF" />
                  <text x="70" y="10" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#27AE60">Mature BA</text>
                  
                  {/* Connection arrows */}
                  <path d="M-30,8 L-15,3" stroke="#27AE60" strokeWidth="1.5" markerEnd="url(#greenArrow)" />
                  <path d="M15,0 L40,7" stroke="#27AE60" strokeWidth="1.5" markerEnd="url(#greenArrow)" />
                  
                  {/* Additional Info */}
                  <text x="0" y="40" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#27AE60">Side-chain Shortening</text>
                </g>
                
                {/* Arrow definitions */}
                <defs>
                  <marker id="greenArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#27AE60" />
                  </marker>
                  <marker id="purpleArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#8E44AD" />
                  </marker>
                  <marker id="blueArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#3498DB" />
                  </marker>
                  <marker id="yellowArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#F1C40F" />
                  </marker>
                  <marker id="redArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#E74C3C" />
                  </marker>
                </defs>
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">β-Oxidation of Fatty Acids</h3>
              <p>While mitochondria are the primary site of fatty acid β-oxidation, peroxisomes specialize in metabolizing very-long-chain fatty acids (VLCFAs) and branched-chain fatty acids. The rate-limiting step is catalyzed by acyl-CoA oxidase (ACOX), which transfers electrons directly to oxygen, generating hydrogen peroxide (H₂O₂).</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="font-medium text-indigo-900 mb-2">ROS Metabolism</h3>
              <p>Peroxisomes both generate and detoxify reactive oxygen species. They contain high concentrations of antioxidant enzymes, particularly catalase, which rapidly converts H₂O₂ to water and oxygen. This dual role creates a delicate balance between oxidative metabolism and protection against oxidative damage.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="font-medium text-purple-900 mb-2">Plasmalogen Synthesis</h3>
              <p>Plasmalogens are a class of phospholipids with a vinyl ether bond at the sn-1 position. They are abundant in myelin sheaths and cardiac tissue. The initial steps of plasmalogen synthesis, particularly the formation of the alkyldihydroxyacetone phosphate (alkyl-DHAP) precursor, occur in peroxisomes.</p>
            </div>
            <div className="bg-rose-50 p-4 rounded-md">
              <h3 className="font-medium text-rose-900 mb-2">Bile Acid Synthesis</h3>
              <p>Peroxisomes are involved in bile acid synthesis, specifically in the side-chain shortening of C27-bile acid intermediates through β-oxidation. Disruptions in this pathway can alter bile acid homeostasis, contributing to hepatic and metabolic disorders.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            The metabolic versatility of peroxisomes positions them at a critical intersection of lipid metabolism, redox homeostasis, and cellular signaling. Their role in processing complex lipids that cannot be handled by mitochondria, combined with their antioxidant functions, makes them essential for cellular health, particularly in metabolically active tissues like the liver, brain, and heart.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 4,
      title: "Systems Biology Perspective",
      content: (
        <>
          <p>Peroxisomes do not function in isolation but maintain intimate connections with other cellular organelles, particularly mitochondria and the endoplasmic reticulum (ER). This interorganellar crosstalk is essential for coordinating complex metabolic processes and maintaining cellular homeostasis.</p>
          
          <ScientificDiagram 
            title="3. Peroxisome-Organelle Interactions" 
            caption="Interorganelle communication networks involving peroxisomes. This diagram illustrates the dynamic interactions between peroxisomes and other cellular organelles, including the exchange of metabolites, signaling molecules, and coordinated actions in lipid metabolism, ROS signaling, and cellular stress responses."
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 550" className="w-full" style={{ maxHeight: '450px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="550" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Peroxisome-Organelle Interactions</text>
                
                {/* Central Peroxisome */}
                <circle cx="400" cy="250" r="70" fill="#F5EEF8" stroke="#8E44AD" strokeWidth="2.5" />
                <text x="400" y="250" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#8E44AD">Peroxisome</text>
                
                {/* Organelles */}
                {/* Mitochondrion */}
                <ellipse cx="200" cy="150" rx="60" ry="40" fill="#D5F5E3" stroke="#27AE60" strokeWidth="2" />
                <text x="200" y="150" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#27AE60">Mitochondrion</text>
                
                {/* ER */}
                <rect x="550" y="150" width="120" height="60" rx="10" fill="#FADBD8" stroke="#E74C3C" strokeWidth="2" />
                <text x="610" y="180" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#E74C3C">ER</text>
                
                {/* Nucleus */}
                <circle cx="200" cy="350" rx="60" ry="40" r="50" fill="#D6EAF8" stroke="#3498DB" strokeWidth="2" />
                <text x="200" y="350" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#3498DB">Nucleus</text>
                
                {/* Lysosome */}
                <circle cx="550" cy="350" r="40" fill="#FCF3CF" stroke="#F1C40F" strokeWidth="2" />
                <text x="550" y="350" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#F1C40F">Lysosome</text>
                
                {/* Connections and Metabolites */}
                {/* Peroxisome-Mitochondrion */}
                <path d="M350,200 L250,165" stroke="#27AE60" strokeWidth="2" strokeDasharray="5,3" />
                <rect x="280" y="165" width="40" height="20" rx="5" fill="#D5F5E3" stroke="#27AE60" strokeWidth="1" />
                <text x="300" y="180" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#27AE60">MCFA</text>
                
                <path d="M250,185 L350,230" stroke="#8E44AD" strokeWidth="2" strokeDasharray="5,3" />
                <rect x="280" y="190" width="40" height="20" rx="5" fill="#F5EEF8" stroke="#8E44AD" strokeWidth="1" />
                <text x="300" y="205" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#8E44AD">VLCFAs</text>
                
                {/* Peroxisome-ER */}
                <path d="M450,200 L550,170" stroke="#E74C3C" strokeWidth="2" strokeDasharray="5,3" />
                <rect x="480" y="165" width="40" height="20" rx="5" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1" />
                <text x="500" y="180" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#E74C3C">Lipids</text>
                
                <path d="M550,190 L450,230" stroke="#8E44AD" strokeWidth="2" strokeDasharray="5,3" />
                <rect x="480" y="195" width="50" height="20" rx="5" fill="#F5EEF8" stroke="#8E44AD" strokeWidth="1" />
                <text x="505" y="210" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#8E44AD">Plasmalogens</text>
                
                {/* Peroxisome-Nucleus */}
                <path d="M350,300 L250,335" stroke="#3498DB" strokeWidth="2" strokeDasharray="5,3" />
                <rect x="280" y="300" width="40" height="20" rx="5" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1" />
                <text x="300" y="315" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">PPARs</text>
                
                <path d="M250,365 L350,330" stroke="#8E44AD" strokeWidth="2" strokeDasharray="5,3" />
                <rect x="270" y="330" width="60" height="20" rx="5" fill="#F5EEF8" stroke="#8E44AD" strokeWidth="1" />
                <text x="300" y="345" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#8E44AD">PEX Genes</text>
                
                {/* Peroxisome-Lysosome */}
                <path d="M450,300 L525,325" stroke="#F1C40F" strokeWidth="2" strokeDasharray="5,3" />
                <text x="500" y="300" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#F1C40F">Pexophagy</text>
                
                {/* Annotations */}
                <rect x="130" y="100" width="140" height="20" rx="5" fill="#f9f9f9" stroke="#27AE60" strokeWidth="1.5" />
                <text x="200" y="115" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#27AE60">Fatty Acid Metabolism</text>
                
                <rect x="540" y="100" width="140" height="20" rx="5" fill="#f9f9f9" stroke="#E74C3C" strokeWidth="1.5" />
                <text x="610" y="115" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#E74C3C">Membrane Biogenesis</text>
                
                <rect x="130" y="400" width="140" height="20" rx="5" fill="#f9f9f9" stroke="#3498DB" strokeWidth="1.5" />
                <text x="200" y="415" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#3498DB">Transcriptional Regulation</text>
                
                <rect x="480" y="400" width="140" height="20" rx="5" fill="#f9f9f9" stroke="#F1C40F" strokeWidth="1.5" />
                <text x="550" y="415" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#F1C40F">Quality Control</text>
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">Peroxisome-Mitochondria Crosstalk</h3>
              <p>Peroxisomes maintain close contact with mitochondria, facilitating the transfer of metabolites like fatty acyl-CoAs and ROS signals. Super-resolution microscopy has revealed "kiss-and-run" interactions that enable rapid exchange of partially oxidized substrates. This synergy is particularly important for efficient fatty acid oxidation.</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="font-medium text-indigo-900 mb-2">Peroxisome-ER Axis</h3>
              <p>The partnership between peroxisomes and the ER is evident in plasmalogen synthesis and lipid trafficking. Nascent peroxisomes can bud from specialized ER subdomains, establishing a direct lineage between these organelles. This axis also coordinates phospholipid and cholesterol metabolism.</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="font-medium text-purple-900 mb-2">Peroxisome-Nucleus Signaling</h3>
              <p>Transcriptional regulation of peroxisomal function involves peroxisome proliferator-activated receptors (PPARs), especially PPAR-α. When activated by fatty acids or synthetic ligands (e.g., fibrates), PPAR-α upregulates genes involved in peroxisomal β-oxidation and lipid metabolism.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            The integration of peroxisomes into cellular metabolic networks highlights the importance of organelle cooperation in maintaining homeostasis. Disruptions in one organelle can cascade to affect others, potentially exacerbating cellular dysfunction. This systems biology perspective is crucial for understanding the complex pathophysiology of peroxisomal disorders and developing targeted therapeutic strategies.
          </KeyTakeaway>
          
          <p>In conditions like non-alcoholic fatty liver disease (NAFLD), the interplay between peroxisomes, mitochondria, and the ER becomes particularly relevant. Lipotoxicity can impair peroxisomal function, leading to accumulation of VLCFAs and increased oxidative stress, which in turn affects mitochondrial function and contributes to ER stress, creating a vicious cycle of organelle dysfunction.</p>
        </>
      )
    },
    {
      id: 5,
      title: "Clinical Relevance: Peroxisomal Disorders",
      content: (
        <>
          <p>Peroxisomal disorders encompass a spectrum of genetic diseases ranging from severe, multi-system conditions present at birth to milder, more progressive disorders manifesting later in life. Understanding the molecular basis of these conditions provides insights into the critical roles of peroxisomes in human physiology.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">Peroxisomal Biogenesis Disorders (PBDs)</h3>
              <p>Zellweger spectrum disorders are the most severe PBDs, caused by mutations in PEX genes, resulting in absent or severely dysfunctional peroxisomes. Clinical features include hypotonia, craniofacial abnormalities, liver dysfunction, and neurodevelopmental defects. The severity ranges from lethal Zellweger syndrome to milder forms like infantile Refsum disease.</p>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="font-medium text-indigo-900 mb-2">X-Linked Adrenoleukodystrophy (X-ALD)</h3>
              <p>X-ALD results from mutations in the ABCD1 gene encoding the peroxisomal membrane transporter ALDP. The disease leads to accumulation of VLCFAs, primarily affecting the central nervous system and adrenal cortex. Clinical phenotypes include childhood cerebral ALD (rapid demyelination) and adrenomyeloneuropathy (progressive spinal cord degeneration in adults).</p>
            </div>
          </div>
          
          <ScientificDiagram 
            title="4. X-ALD Pathophysiology" 
            caption="Pathophysiological mechanisms in X-linked adrenoleukodystrophy. A) Normal peroxisomal VLCFA import and metabolism. B) Defective ABCD1 transporter in X-ALD leads to accumulation of VLCFAs, resulting in lipotoxicity, oxidative stress, and inflammatory responses that drive the disease pathology."
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 550" className="w-full" style={{ maxHeight: '450px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="550" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">X-ALD Pathophysiology</text>
                
                {/* A. Normal Peroxisome */}
                <g transform="translate(200, 175)">
                  {/* Background */}
                  <rect x="-125" y="-75" width="250" height="200" rx="10" fill="#F5EEF8" stroke="#8E44AD" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-55" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#8E44AD">A. Normal VLCFA Metabolism</text>
                  
                  {/* Peroxisome */}
                  <ellipse cx="0" cy="20" rx="80" ry="60" fill="#f9f9f9" stroke="#8E44AD" strokeWidth="2" />
                  <text x="0" y="-15" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#8E44AD">Peroxisome</text>
                  
                  {/* ABCD1 transporter */}
                  <rect x="-85" y="20" width="30" height="15" rx="7" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="-70" y="29" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#8E44AD">ABCD1</text>
                  
                  {/* VLCFA */}
                  <rect x="-110" y="45" width="40" height="15" rx="5" fill="#FCF3CF" />
                  <text x="-90" y="55" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#F1C40F">VLCFA</text>
                  <path d="M-90,40 L-80,35" stroke="#F1C40F" strokeWidth="1.5" markerEnd="url(#yellowArrow)" />
                  
                  {/* β-oxidation */}
                  <rect x="0" y="20" width="30" height="30" rx="15" fill="#D2B4DE" stroke="#8E44AD" strokeWidth="1.5" />
                  <text x="0" y="32" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#8E44AD">β-oxid</text>
                  <path d="M-55,27 L-30,27" stroke="#8E44AD" strokeWidth="1.5" markerEnd="url(#purpleArrow)" />
                  
                  {/* Products */}
                  <rect x="50" y="20" width="40" height="15" rx="5" fill="#AED6F1" />
                  <text x="70" y="30" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#3498DB">MCFA</text>
                  <path d="M30,27 L50,27" stroke="#3498DB" strokeWidth="1.5" markerEnd="url(#blueArrow)" />
                  
                  <rect x="20" y="50" width="40" height="15" rx="5" fill="#F5B7B1" />
                  <text x="40" y="60" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#E74C3C">H₂O₂</text>
                  <path d="M15,40 L30,50" stroke="#E74C3C" strokeWidth="1.5" markerEnd="url(#redArrow)" />
                  
                  {/* Peroxisomal Enzymes */}
                  <text x="0" y="80" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#8E44AD">Functional Metabolism</text>
                </g>
                
                {/* B. X-ALD Peroxisome */}
                <g transform="translate(600, 175)">
                  {/* Background */}
                  <rect x="-125" y="-75" width="250" height="200" rx="10" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-55" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#E74C3C">B. X-ALD Pathology</text>
                  
                  {/* Peroxisome */}
                  <ellipse cx="0" cy="20" rx="80" ry="60" fill="#f9f9f9" stroke="#E74C3C" strokeWidth="2" />
                  <text x="0" y="-15" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#E74C3C">Peroxisome</text>
                  
                  {/* Mutated ABCD1 transporter */}
                  <rect x="-85" y="20" width="30" height="15" rx="7" fill="#F5B7B1" stroke="#E74C3C" strokeWidth="1.5" />
                  <text x="-70" y="29" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#E74C3C">ABCD1</text>
                  <text x="-70" y="40" textAnchor="middle" fontSize="10" fill="#E74C3C">✕</text>
                  
                  {/* Accumulated VLCFA */}
                  <rect x="-110" y="45" width="40" height="15" rx="5" fill="#FCF3CF" />
                  <text x="-90" y="55" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#F1C40F">VLCFA</text>
                  
                  <rect x="-80" y="65" width="40" height="15" rx="5" fill="#FCF3CF" />
                  <text x="-60" y="75" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#F1C40F">VLCFA</text>
                  
                  <rect x="-40" y="55" width="40" height="15" rx="5" fill="#FCF3CF" />
                  <text x="-20" y="65" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#F1C40F">VLCFA</text>
                  
                  {/* Consequences */}
                  <rect x="40" y="-10" width="70" height="15" rx="5" fill="#F5B7B1" />
                  <text x="75" y="0" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#E74C3C">Lipotoxicity</text>
                  
                  <rect x="40" y="20" width="70" height="15" rx="5" fill="#F5B7B1" />
                  <text x="75" y="30" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#E74C3C">Oxidative Stress</text>
                  
                  <rect x="40" y="50" width="70" height="15" rx="5" fill="#F5B7B1" />
                  <text x="75" y="60" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#E74C3C">Inflammation</text>
                  
                  {/* Consequences Text */}
                  <text x="0" y="95" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#E74C3C">VLCFA Accumulation</text>
                </g>
                
                {/* Clinical Outcomes */}
                <g transform="translate(400, 350)">
                  <rect x="-300" y="-25" width="600" height="70" rx="10" fill="#f9f9f9" stroke="#333" strokeWidth="1.5" />
                  
                  <text x="0" y="-5" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#111">Clinical Manifestations</text>
                  
                  <g transform="translate(-225, 25)">
                    <rect x="-50" y="-15" width="100" height="30" rx="5" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1.5" />
                    <text x="0" y="0" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#2874A6">Demyelination</text>
                  </g>
                  
                  <g transform="translate(-75, 25)">
                    <rect x="-50" y="-15" width="100" height="30" rx="5" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1.5" />
                    <text x="0" y="0" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#2874A6">Neurodegeneration</text>
                  </g>
                  
                  <g transform="translate(75, 25)">
                    <rect x="-50" y="-15" width="100" height="30" rx="5" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1.5" />
                    <text x="0" y="0" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#2874A6">Adrenal Insufficiency</text>
                  </g>
                  
                  <g transform="translate(225, 25)">
                    <rect x="-50" y="-15" width="100" height="30" rx="5" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1.5" />
                    <text x="0" y="0" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#2874A6">Axonopathy</text>
                  </g>
                </g>
                
                {/* Connecting arrows */}
                <path d="M275,225 L525,225" stroke="#333" strokeWidth="2" />
                <text x="400" y="215" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#333">ABCD1 Mutation</text>
                
                <path d="M600,250 L600,310 L500,325" stroke="#E74C3C" strokeWidth="2" markerEnd="url(#redArrow)" />
                <path d="M600,250 L600,310 L300,325" stroke="#E74C3C" strokeWidth="2" markerEnd="url(#redArrow)" />
                
                {/* Arrow definitions */}
                <defs>
                  <marker id="greenArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#27AE60" />
                  </marker>
                  <marker id="purpleArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#8E44AD" />
                  </marker>
                  <marker id="blueArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#3498DB" />
                  </marker>
                  <marker id="yellowArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#F1C40F" />
                  </marker>
                  <marker id="redArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#E74C3C" />
                  </marker>
                </defs>
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-purple-50 p-4 rounded-md">
              <h3 className="font-medium text-purple-900 mb-2">Hyperoxaluria</h3>
              <p>Primary hyperoxaluria type 1 involves the liver-specific enzyme alanine-glyoxylate aminotransferase (AGT), which is partially localized to peroxisomes. Mis-targeting of AGT disrupts glyoxylate detoxification, increasing oxalate production and leading to kidney stones, nephrocalcinosis, and eventual renal failure.</p>
            </div>
            <div className="bg-rose-50 p-4 rounded-md">
              <h3 className="font-medium text-rose-900 mb-2">Metabolic Syndrome and NAFLD/NASH</h3>
              <p>Emerging evidence suggests a link between peroxisomal dysfunction and common metabolic conditions like non-alcoholic fatty liver disease (NAFLD) and non-alcoholic steatohepatitis (NASH). Hepatic peroxisomes often proliferate in fatty liver disease as a compensatory response to rising lipid flux, but chronic lipotoxicity and oxidative stress can eventually impair peroxisomal function.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            The spectrum of peroxisomal disorders—from severe congenital syndromes to more subtle metabolic dysfunction in common conditions—underscores the critical role of peroxisomes in human physiology. The complex pathophysiology of these disorders often involves cascading effects on multiple cellular processes, including lipid metabolism, redox homeostasis, and inflammation, highlighting the need for integrated therapeutic approaches.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 6,
      title: "Biomarkers and Therapeutic Strategies",
      content: (
        <>
          <p>Accurate diagnosis and effective management of peroxisomal disorders depend on reliable biomarkers and targeted therapeutic approaches. Advances in our understanding of peroxisomal biology have led to the development of various diagnostic tools and treatment strategies.</p>
          
          <div className="space-y-6 mb-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-3">Key Biomarkers for Peroxisomal Function</h3>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Biomarker</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clinical Utility</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-blue-700">VLCFA Profile</td>
                      <td className="px-6 py-4">Plasma or fibroblast measurement of very-long-chain fatty acids. Elevated C26:0 and increased C24:0/C22:0 ratio.</td>
                      <td className="px-6 py-4">Primary marker for X-ALD and other peroxisomal β-oxidation defects.</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-blue-700">Plasmalogen Levels</td>
                      <td className="px-6 py-4">Measurement of plasmalogen-derived phospholipids in erythrocyte membranes.</td>
                      <td className="px-6 py-4">Reduced levels in peroxisomal biogenesis disorders, rhizomelic chondrodysplasia punctata.</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-blue-700">Bile Acid Metabolites</td>
                      <td className="px-6 py-4">Mass spectrometry analysis of bile acid profiles, looking for atypical intermediates.</td>
                      <td className="px-6 py-4">Diagnosis of bile acid synthesis defects related to peroxisomal dysfunction.</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-blue-700">Oxidative Stress Markers</td>
                      <td className="px-6 py-4">Malondialdehyde (MDA), F₂-isoprostanes, oxidized LDL, and changes in catalase/SOD activity.</td>
                      <td className="px-6 py-4">Indirect measure of peroxisomal stress, particularly useful in metabolic disorders.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          
          <ScientificDiagram 
            title="5. Therapeutic Approaches for Peroxisomal Disorders" 
            caption="Overview of current and emerging therapeutic strategies for peroxisomal disorders. A) Dietary and lifestyle interventions. B) Pharmacological agents, including PPAR-α agonists and bile acid supplements. C) Advanced therapies, including hematopoietic stem cell transplantation and gene therapy. D) Frontier approaches, including peptide therapies, NAD+ precursors, and microbiome modulators."
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 550" className="w-full" style={{ maxHeight: '450px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="550" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Therapeutic Approaches for Peroxisomal Disorders</text>
                
                {/* A. Dietary/Lifestyle */}
                <g transform="translate(200, 140)">
                  {/* Background */}
                  <rect x="-120" y="-70" width="240" height="160" rx="10" fill="#f9f9f9" stroke="#F1C40F" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-45" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#F1C40F">A. Dietary & Lifestyle</text>
                  
                  {/* Content */}
                  <g transform="translate(0, -10)">
                    <rect x="-100" y="0" width="200" height="30" rx="5" fill="#FCF3CF" stroke="#F1C40F" strokeWidth="1" />
                    <text x="0" y="20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#7D6608">VLCFA Restriction</text>
                  </g>
                  
                  <g transform="translate(0, 40)">
                    <rect x="-100" y="0" width="200" height="30" rx="5" fill="#FCF3CF" stroke="#F1C40F" strokeWidth="1" />
                    <text x="0" y="20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#7D6608">Lorenzo's Oil (X-ALD)</text>
                  </g>
                  
                  <g transform="translate(0, 90)">
                    <rect x="-100" y="0" width="200" height="30" rx="5" fill="#FCF3CF" stroke="#F1C40F" strokeWidth="1" />
                    <text x="0" y="20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#7D6608">Antioxidant-Rich Foods</text>
                  </g>
                </g>
                
                {/* B. Pharmacological */}
                <g transform="translate(600, 140)">
                  {/* Background */}
                  <rect x="-120" y="-70" width="240" height="160" rx="10" fill="#f9f9f9" stroke="#3498DB" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-45" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#3498DB">B. Pharmacological</text>
                  
                  {/* Content */}
                  <g transform="translate(0, -10)">
                    <rect x="-100" y="0" width="200" height="30" rx="5" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1" />
                    <text x="0" y="20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#2874A6">PPAR-α Agonists (Fibrates)</text>
                  </g>
                  
                  <g transform="translate(0, 40)">
                    <rect x="-100" y="0" width="200" height="30" rx="5" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1" />
                    <text x="0" y="20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#2874A6">Steroid Replacement</text>
                  </g>
                  
                  <g transform="translate(0, 90)">
                    <rect x="-100" y="0" width="200" height="30" rx="5" fill="#D6EAF8" stroke="#3498DB" strokeWidth="1" />
                    <text x="0" y="20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#2874A6">Bile Acid Supplements</text>
                  </g>
                </g>
                
                {/* C. Advanced Therapies */}
                <g transform="translate(200, 360)">
                  {/* Background */}
                  <rect x="-120" y="-70" width="240" height="160" rx="10" fill="#f9f9f9" stroke="#27AE60" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-45" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#27AE60">C. Advanced Therapies</text>
                  
                  {/* Content */}
                  <g transform="translate(0, -10)">
                    <rect x="-100" y="0" width="200" height="30" rx="5" fill="#D4EFDF" stroke="#27AE60" strokeWidth="1" />
                    <text x="0" y="20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#196F3D">Hematopoietic Stem Cell Tx</text>
                  </g>
                  
                  <g transform="translate(0, 40)">
                    <rect x="-100" y="0" width="200" height="30" rx="5" fill="#D4EFDF" stroke="#27AE60" strokeWidth="1" />
                    <text x="0" y="20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#196F3D">Gene Therapy (ABCD1)</text>
                  </g>
                  
                  <g transform="translate(0, 90)">
                    <rect x="-100" y="0" width="200" height="30" rx="5" fill="#D4EFDF" stroke="#27AE60" strokeWidth="1" />
                    <text x="0" y="20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#196F3D">Enzyme Replacement</text>
                  </g>
                </g>
                
                {/* D. Frontier Approaches */}
                <g transform="translate(600, 360)">
                  {/* Background */}
                  <rect x="-120" y="-70" width="240" height="160" rx="10" fill="#f9f9f9" stroke="#8E44AD" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-45" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#8E44AD">D. Frontier Approaches</text>
                  
                  {/* Content */}
                  <g transform="translate(0, -10)">
                    <rect x="-100" y="0" width="200" height="30" rx="5" fill="#F5EEF8" stroke="#8E44AD" strokeWidth="1" />
                    <text x="0" y="20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#8E44AD">SS Peptides (Mitochondrial)</text>
                  </g>
                  
                  <g transform="translate(0, 40)">
                    <rect x="-100" y="0" width="200" height="30" rx="5" fill="#F5EEF8" stroke="#8E44AD" strokeWidth="1" />
                    <text x="0" y="20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#8E44AD">NAD+ Precursors (NR, NMN)</text>
                  </g>
                  
                  <g transform="translate(0, 90)">
                    <rect x="-100" y="0" width="200" height="30" rx="5" fill="#F5EEF8" stroke="#8E44AD" strokeWidth="1" />
                    <text x="0" y="20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#8E44AD">Selective PPAR Modulators</text>
                  </g>
                </g>
                
                {/* Center Node - Patient */}
                <circle cx="400" cy="250" r="40" fill="#FADBD8" stroke="#E74C3C" strokeWidth="2.5" />
                <text x="400" y="245" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#E74C3C">Patient with</text>
                <text x="400" y="265" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#E74C3C">Peroxisomal</text>
                <text x="400" y="285" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#E74C3C">Disorder</text>
                
                {/* Connections */}
                <path d="M350,225 L250,200" stroke="#F1C40F" strokeWidth="2" strokeDasharray="5,3" />
                <path d="M450,225 L550,200" stroke="#3498DB" strokeWidth="2" strokeDasharray="5,3" />
                <path d="M350,275 L250,310" stroke="#27AE60" strokeWidth="2" strokeDasharray="5,3" />
                <path d="M450,275 L550,310" stroke="#8E44AD" strokeWidth="2" strokeDasharray="5,3" />
              </svg>
            </div>
          </ScientificDiagram>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="font-medium text-blue-900 mb-2">Current Therapeutic Strategies</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Dietary Interventions:</strong> VLCFA restriction and Lorenzo's oil for X-ALD; ketogenic diet for some peroxisomal disorders.</li>
                <li><strong>Pharmacological Agents:</strong> PPAR-α agonists (fibrates) to enhance peroxisomal β-oxidation; steroid replacement for adrenal insufficiency; bile acid supplements.</li>
                <li><strong>Hematopoietic Stem Cell Transplantation (HSCT):</strong> Can halt progression of demyelination in early-stage childhood cerebral X-ALD.</li>
                <li><strong>Gene Therapy:</strong> Lentiviral vectors to correct ABCD1 mutations in X-ALD are showing promise in early studies.</li>
              </ul>
            </div>
            <div className="bg-indigo-50 p-4 rounded-md">
              <h3 className="font-medium text-indigo-900 mb-2">Frontier Approaches</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Mitochondria-Targeting Peptides:</strong> SS peptides like SS-31 may provide cross-protection for peroxisomes due to organelle interdependence.</li>
                <li><strong>NAD+ Precursors:</strong> Nicotinamide riboside (NR) or nicotinamide mononucleotide (NMN) may support global cellular metabolism and potentially bolster peroxisomal function.</li>
                <li><strong>Selective PPAR Modulators (SPPARMs):</strong> Next-generation modulators like pemafibrate offer more selective regulation of PPAR-α target genes.</li>
                <li><strong>Microbiome Modulators:</strong> Targeting gut microbiota to influence production of short-chain fatty acids that may benefit peroxisomal function.</li>
              </ul>
            </div>
          </div>
          
          <KeyTakeaway>
            The management of peroxisomal disorders requires an integrated approach that addresses the specific molecular defect while supporting overall cellular homeostasis. While curative therapies remain limited for many peroxisomal disorders, advances in gene therapy, stem cell transplantation, and novel pharmacological agents are expanding the therapeutic landscape. Additionally, supportive measures targeting downstream consequences—such as antioxidant therapy for oxidative stress and anti-inflammatory interventions—can help mitigate disease progression.
          </KeyTakeaway>
          
          <p>Implementation in clinical practice involves risk stratification (genetic testing, VLCFA measurements, liver function tests), integrated therapeutic protocols combining lifestyle modifications with appropriate pharmacological support, and regular monitoring of disease biomarkers and functional assessments to track progress and adjust treatment strategies as needed.</p>
        </>
      )
    },
    {
      id: 7,
      title: "Conclusion and Future Directions",
      content: (
        <>
          <p>Peroxisomes represent a critical intersection of lipid metabolism, redox homeostasis, and cellular signaling. Their involvement in multiple metabolic pathways positions them as key players in both rare genetic disorders and more common metabolic conditions. As our understanding of peroxisomal biology continues to deepen, new opportunities for diagnostic and therapeutic innovation emerge.</p>
          
          <div className="space-y-6 mb-6">
            <div>
              <h3 className="text-xl font-medium mb-3">Key Insights from Peroxisomal Biology</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Peroxisomes are essential organelles with multiple metabolic functions, particularly in lipid metabolism and ROS detoxification.</li>
                <li>They maintain intimate connections with other cellular organelles, especially mitochondria and the ER, creating an integrated metabolic network.</li>
                <li>Peroxisomal dysfunction can manifest as severe genetic disorders or contribute to more common metabolic conditions like NAFLD/NASH.</li>
                <li>The molecular mechanisms underlying peroxisomal disorders provide insights into fundamental aspects of cellular physiology and potential therapeutic targets.</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-medium mb-3">Future Research Directions</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Systems Biology Approaches:</strong> Further exploration of peroxisome-organelle interactions using advanced imaging techniques and proteomics.</li>
                <li><strong>Personalized Medicine:</strong> Development of more targeted therapies based on specific genetic mutations and molecular mechanisms.</li>
                <li><strong>Novel Biomarkers:</strong> Identification of more sensitive and specific biomarkers for early detection and monitoring of peroxisomal dysfunction.</li>
                <li><strong>Metabolic Reprogramming:</strong> Strategies to enhance peroxisomal biogenesis and function in conditions of metabolic stress.</li>
                <li><strong>Advanced Gene Therapy:</strong> Refinement of gene therapy approaches for peroxisomal disorders, including CRISPR-based technologies.</li>
              </ul>
            </div>
          </div>
          
          <ScientificDiagram 
            title="6. Peroxisomes in Health and Disease: A Comprehensive View" 
            caption="Integrated overview of peroxisomal functions, dysfunctions, and therapeutic approaches. This schematic illustrates the critical balance between peroxisomal health and disease, highlighting key molecular mechanisms, clinical manifestations, diagnostic biomarkers, and therapeutic interventions across the spectrum of peroxisomal disorders."
          >
            <div className="flex justify-center py-4">
              <svg viewBox="0 0 800 550" className="w-full" style={{ maxHeight: '450px' }}>
                {/* Background */}
                <rect x="0" y="0" width="800" height="550" fill="#ffffff" />
                
                {/* Title */}
                <text x="400" y="50" textAnchor="middle" fontSize="20" fontWeight="bold" fill="#333333">Peroxisomes in Health and Disease: A Comprehensive View</text>
                
                {/* Healthy Peroxisome Side */}
                <g transform="translate(200, 160)">
                  {/* Background */}
                  <rect x="-150" y="-80" width="300" height="340" rx="10" fill="#EBF5FB" stroke="#3498DB" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-60" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#3498DB">PEROXISOMAL HEALTH</text>
                  
                  {/* Healthy Peroxisome */}
                  <circle cx="0" cy="-10" r="40" fill="#D6EAF8" stroke="#3498DB" strokeWidth="2" />
                  <text x="0" y="-10" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#3498DB">Functional</text>
                  <text x="0" y="10" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#3498DB">Peroxisome</text>
                  
                  {/* Key Functions */}
                  <g transform="translate(0, 60)">
                    <rect x="-120" y="0" width="240" height="30" rx="5" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                    <text x="0" y="20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#2874A6">Efficient VLCFA β-oxidation</text>
                  </g>
                  
                  <g transform="translate(0, 100)">
                    <rect x="-120" y="0" width="240" height="30" rx="5" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                    <text x="0" y="20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#2874A6">ROS Detoxification</text>
                  </g>
                  
                  <g transform="translate(0, 140)">
                    <rect x="-120" y="0" width="240" height="30" rx="5" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                    <text x="0" y="20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#2874A6">Plasmalogen Synthesis</text>
                  </g>
                  
                  <g transform="translate(0, 180)">
                    <rect x="-120" y="0" width="240" height="30" rx="5" fill="#AED6F1" stroke="#3498DB" strokeWidth="1.5" />
                    <text x="0" y="20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#2874A6">Bile Acid Metabolism</text>
                  </g>
                  
                  {/* Overall Result */}
                  <g transform="translate(0, 230)">
                    <rect x="-120" y="0" width="240" height="30" rx="10" fill="#D4EFDF" stroke="#27AE60" strokeWidth="1.5" />
                    <text x="0" y="20" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#27AE60">Cellular Homeostasis</text>
                  </g>
                </g>
                
                {/* Dysfunctional Peroxisome Side */}
                <g transform="translate(600, 160)">
                  {/* Background */}
                  <rect x="-150" y="-80" width="300" height="340" rx="10" fill="#FADBD8" stroke="#E74C3C" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-60" textAnchor="middle" fontSize="18" fontWeight="bold" fill="#E74C3C">PEROXISOMAL DYSFUNCTION</text>
                  
                  {/* Dysfunctional Peroxisome */}
                  <circle cx="0" cy="-10" r="40" fill="#F5B7B1" stroke="#E74C3C" strokeWidth="2" />
                  <text x="0" y="-10" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#E74C3C">Impaired</text>
                  <text x="0" y="10" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#E74C3C">Peroxisome</text>
                  
                  {/* Key Dysfunctions */}
                  <g transform="translate(0, 60)">
                    <rect x="-120" y="0" width="240" height="30" rx="5" fill="#F5B7B1" stroke="#E74C3C" strokeWidth="1.5" />
                    <text x="0" y="20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#922B21">VLCFA Accumulation</text>
                  </g>
                  
                  <g transform="translate(0, 100)">
                    <rect x="-120" y="0" width="240" height="30" rx="5" fill="#F5B7B1" stroke="#E74C3C" strokeWidth="1.5" />
                    <text x="0" y="20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#922B21">Oxidative Stress</text>
                  </g>
                  
                  <g transform="translate(0, 140)">
                    <rect x="-120" y="0" width="240" height="30" rx="5" fill="#F5B7B1" stroke="#E74C3C" strokeWidth="1.5" />
                    <text x="0" y="20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#922B21">Reduced Plasmalogens</text>
                  </g>
                  
                  <g transform="translate(0, 180)">
                    <rect x="-120" y="0" width="240" height="30" rx="5" fill="#F5B7B1" stroke="#E74C3C" strokeWidth="1.5" />
                    <text x="0" y="20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#922B21">Impaired Bile Acid Synthesis</text>
                  </g>
                  
                  {/* Overall Result */}
                  <g transform="translate(0, 230)">
                    <rect x="-120" y="0" width="240" height="30" rx="10" fill="#FADBD8" stroke="#C0392B" strokeWidth="1.5" />
                    <text x="0" y="20" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#C0392B">Cellular Dysfunction</text>
                  </g>
                </g>
                
                {/* Central Panel - Bridge */}
                <g transform="translate(400, 230)">
                  {/* Background */}
                  <rect x="-100" y="-100" width="200" height="200" rx="10" fill="#f9f9f9" stroke="#333" strokeWidth="1.5" />
                  
                  {/* Title */}
                  <text x="0" y="-70" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#111">TRANSITION</text>
                  
                  {/* Biomarkers */}
                  <text x="0" y="-30" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#8E44AD">Biomarkers</text>
                  <text x="0" y="-10" textAnchor="middle" fontSize="12" fill="#8E44AD">• VLCFA Profile</text>
                  <text x="0" y="10" textAnchor="middle" fontSize="12" fill="#8E44AD">• Plasmalogen Levels</text>
                  <text x="0" y="30" textAnchor="middle" fontSize="12" fill="#8E44AD">• Oxidative Stress Markers</text>
                  
                  {/* Therapeutic Interventions */}
                  <text x="0" y="60" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#27AE60">Interventions</text>
                  <text x="0" y="80" textAnchor="middle" fontSize="12" fill="#27AE60">↑ PPAR-α Signaling</text>
                </g>
                
                {/* Arrow Connection */}
                <path d="M350,120 L450,120" stroke="#333" strokeWidth="2" markerEnd="url(#rightArrow)" />
                <path d="M450,170 L350,170" stroke="#333" strokeWidth="2" markerEnd="url(#leftArrow)" />
                
                {/* Disease Manifestations */}
                <g transform="translate(400, 450)">
                  <rect x="-300" y="-30" width="600" height="60" rx="10" fill="#f9f9f9" stroke="#333" strokeWidth="1.5" />
                  
                  <text x="0" y="-10" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#111">Clinical Spectrum</text>
                  
                  <g transform="translate(-225, 15)">
                    <text x="0" y="0" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#C0392B">X-ALD</text>
                  </g>
                  
                  <g transform="translate(-125, 15)">
                    <text x="0" y="0" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#C0392B">Zellweger</text>
                  </g>
                  
                  <g transform="translate(-25, 15)">
                    <text x="0" y="0" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#C0392B">Hyperoxaluria</text>
                  </g>
                  
                  <g transform="translate(100, 15)">
                    <text x="0" y="0" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#C0392B">NAFLD/NASH</text>
                  </g>
                  
                  <g transform="translate(200, 15)">
                    <text x="0" y="0" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#C0392B">Neurodegenerative</text>
                  </g>
                </g>
                
                {/* Arrow definitions */}
                <defs>
                  <marker id="rightArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#333" />
                  </marker>
                  <marker id="leftArrow" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                    <polygon points="10 0, 0 3.5, 10 7" fill="#333" />
                  </marker>
                </defs>
              </svg>
            </div>
          </ScientificDiagram>
          
          <KeyTakeaway>
            Peroxisomes represent a fascinating nexus of cellular metabolism, redox balance, and signaling pathways. As we continue to unravel the complexities of peroxisomal biology, we gain new insights into both rare genetic disorders and more common metabolic conditions. This knowledge forms the foundation for developing innovative diagnostic tools and targeted therapeutic approaches that may ultimately transform the management of peroxisomal disorders and related conditions.
          </KeyTakeaway>
          
          <p>In conclusion, the study of peroxisomes has evolved from basic organelle biology to a rich field with significant clinical implications. The interplay between peroxisomes and other cellular compartments underscores the importance of an integrated, systems-based approach to understanding cellular physiology and pathology. As we look to the future, continued advances in genomics, proteomics, lipidomics, and therapeutic development promise to further expand our ability to diagnose and treat conditions involving peroxisomal dysfunction, ultimately improving patient outcomes across the spectrum of peroxisomal disorders.</p>
        </>
      )
    }
  ];
  
  return (
    <MentorshipAccessGate>
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-serif font-bold text-gray-800 mb-2">Peroxisomes in Health and Disease</h1>
              <p className="text-lg text-gray-600">A Systems-Based, Clinically-Relevant Exploration</p>
            </div>
            
            <div className="bg-gradient-to-r from-green-500 to-blue-500 text-white px-3 py-1 rounded-full flex items-center text-sm mt-2 md:mt-0">
              <CheckCircle2 className="h-4 w-4 mr-1" />
              <span>Mentorship Content</span>
            </div>
          </div>
          
          <Separator className="mb-6" />
          
          <Slideshow slides={lectureSlides} />
          
          {/* Audio Embed */}
          <div className="mt-8">
            <h3 className="text-lg font-medium mb-4">Lecture Audio</h3>
            <div className="flex justify-center">
              <iframe 
                src="https://share.descript.com/embed/vpx2MVnPAuq" 
                width="640" 
                height="360" 
                frameBorder="0" 
                allowFullScreen
                className="rounded-lg shadow-sm"
                title="Peroxisomes in Health and Disease - Lecture Audio"
              />
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
    </MentorshipAccessGate>
  );
}

export default PeroxisomesHealthDisease;